import type {
  OnChangeFn,
  Row,
  RowSelectionState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  ButtonRow,
  DataTable,
  Icon,
  useDeferredChangeEffect,
  useEffectOnValueChange,
  useMountEffect,
} from '@timelyapp/react';
import classNames from 'classnames';
import {
  type AggregateFunctionName,
  tagDisplayToColumnMode,
} from 'components/datatable/datatable.types';
import { useDatatableState } from 'components/datatable/useDatatableState';
import Emoji from 'components/Emoji';
import { Clock } from 'components/Icons';
import InlineMessage from 'components/InlineMessage';
import SplashMessage from 'components/SplashMessage';
import LoggedEntryBatchActions from 'components/superreports/LoggedEntryBatchActions';
import QuickExportButton from 'components/superreports/ReportExport/QuickExportButton';
import ReportQueryDateOptions from 'components/superreports/ReportQueryDateOptions';
import ReportQueryFilters from 'components/superreports/ReportQueryFilters';
import type { HourColumn, TaskColumn } from 'constants/export';
import {
  COLUMN_LINKED_WORK_ID,
  COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD,
  COLUMN_LINKED_WORK_STATUS,
  COLUMN_LINKED_WORK_TITLE,
  COLUMN_STATE,
} from 'constants/filters';
import { reportQueryFactory } from 'factories/filter';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { createHourDraftFromId } from 'redux/modules/hours.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import {
  exportTimesheetData,
  fetchDayPropertiesForTimesheets,
  fetchTimesheetsForDatatable,
  updateTimesheetColumns,
} from 'redux/modules/superreports.actions';
import type { ExportReportTrackingSource, ReportQuery } from 'redux/modules/superreports.types';
import {
  batchSelectionActions,
  restoreTimesheetQuery,
  saveTimesheetQuery,
  setQuery,
} from 'redux/modules/superreports.types';
import type { TimesheetsDatatableRow } from 'redux/modules/superreports-datatable.types';

import { saveUserProperty } from 'redux/modules/user.actions';
import { getTimesheet } from 'redux/selectors/superreports';
import type { Account } from 'types/account';
import type { MappedActions } from 'types/redux';
import type { DeepPartial } from 'types/utility';
import aggregateFunctions from 'utils/aggregate-helper';
import { getLinkedWorkParentColumn, isLinkedWorkColumn } from 'utils/linked-work';
import { getStartOfWeek } from '../../redux/selectors/accounts';
import styles from './TimesheetPage.module.scss';
import { useTimesheetColumnDefinitions } from './TimesheetsDatatable/columns';
import DatatableOptionsDropdown from './TimesheetsDatatable/DatatableOptions/DatatableOptionsDropdown';
import { getDefaultTimesheetDatatableState } from './TimesheetsDatatable/defaultState';
import TimesheetDatatableFooter from './TimesheetsDatatable/TimesheetDatatableFooter';
import { useTableOptionsResetWarning } from './TimesheetsDatatable/useTableOptionsResetWarning';
import { TimesheetScopes } from './types';

const getRowId = (row: TimesheetsDatatableRow) => '' + row.id; // Tanstack expects string ids
const getScopeName = (scope: TimesheetScopes) =>
  Object.keys(TimesheetScopes).find(key => TimesheetScopes[key] === scope);

const timesheetColumnIdToExportColumnIdMap = {
  day: 'hour_date',
  tags: 'hour_labels',
  notes: 'hour_note',
  hour_billed: 'billable',
  state: 'hour_state',
} as const;
const convertTimesheetColumnIdToExportColumnId = <T extends HourColumn | TaskColumn>(
  columns: Array<T>
) =>
  columns.map(c => {
    // Handle static mappings
    if (c in timesheetColumnIdToExportColumnIdMap) {
      return timesheetColumnIdToExportColumnIdMap[
        c as keyof typeof timesheetColumnIdToExportColumnIdMap
      ];
    }
    // Handle dynamic linked work columns - keep the same format
    if (typeof c === 'string' && c.match(/^linked_work_(id|title|status|jira_custom_field)_\d+$/)) {
      return c;
    }
    return c;
  });

const mapStateToProps = getTimesheet;
const mapActionsToProps = {
  clearSelection: batchSelectionActions.clearSelection,
  createHourDraftFromId,
  exportTimesheetData,
  fetchTimesheetsForDatatable,
  fetchDayPropertiesForTimesheets,
  restoreTimesheetQuery,
  saveTimesheetQuery,
  saveUserProperty,
  setQuery,
  setSelection: batchSelectionActions.setSelection,
  updateTimesheetColumns,
  showSubscriptionNoticeModal,
  toggleSelection: batchSelectionActions.toggleSelection,
} as const;

type Props = ReturnType<typeof mapStateToProps> &
  MappedActions<typeof mapActionsToProps> & {
    account: Account;
    children?: never;
    className?: string;

    /**
     * Controls whether data is ready to be loaded.
     * @default true
     */
    hasInitialized?: boolean;

    /**
     * If true, the "Clear filters" button will not be shown when filters have changed.
     * @default false
     */
    noClearButton?: boolean;

    /**
     * Controls whether the timesheets should be scoped to a user or a project.
     * @default TimesheetScopes.All
     */
    scope?: TimesheetScopes;

    /**
     * Source of the timesheets for tracking events.
     */
    trackingSource: ExportReportTrackingSource;

    /**
     * If true, the query shouldn't be updated on mount/unmount.
     */
    useExistingQuery?: boolean;
  };

const TimesheetPage: React.FC<Props> = ({
  account,
  clearSelection,
  createHourDraftFromId,
  exportTimesheetData,
  fetchTimesheetsForDatatable,
  fetchDayPropertiesForTimesheets,
  hasInitialized = true,
  metadata,
  queryAsFilter,
  query,
  restoreTimesheetQuery,
  saveTimesheetQuery,
  saveUserProperty,
  scope = TimesheetScopes.All,
  setQuery,
  setSelection,
  selectionMap,
  updateTimesheetColumns,
  showSubscriptionNoticeModal,
  toggleSelection,
  useExistingQuery,
  widget,
  ui,
  user,
  reportSettings,
  hours,
  datatableRows,
  totalCount,
  ...props
}) => {
  const updateFilters = useCallback(
    (query: DeepPartial<ReportQuery>) => {
      setQuery({ ...query, page: 1 });
    },
    [setQuery]
  );

  const sorting: SortingState = useMemo(() => {
    return [{ id: widget.columns.sort_by, desc: widget.columns.order === 'desc' }];
  }, [widget.columns.sort_by, widget.columns.order]);

  const handleSortChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const updatedState =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      updateFilters({
        order: updatedState[0].desc ? 'desc' : 'asc',
        sort: updatedState[0].id,
      });

      setSelection({ selectionKey: 'hourIds', value: [] });
    },
    [updateFilters, sorting, setSelection]
  );

  // Enables sharing the reports UI state, so we can reuse components
  useEffect(() => {
    if (useExistingQuery) return;

    restoreTimesheetQuery();
    return () => {
      saveTimesheetQuery();
      clearSelection();
      lastSelectedRowIndex.current = null;
    };
  }, [clearSelection, restoreTimesheetQuery, saveTimesheetQuery, useExistingQuery]);

  useMountEffect(() => {
    if (account.features.hour_states && !widget.columns.names.includes('state')) {
      updateTimesheetColumns([COLUMN_STATE].concat(widget.columns.names) as any);
    }
  });

  const selectionCount = useMemo(() => Object.entries(selectionMap).length, [selectionMap]);

  const lastSelectedRowIndex = useRef<null | number>(null);
  useEffectOnValueChange(selectionMap, () => {
    if (selectionCount === 0) {
      lastSelectedRowIndex.current = null;
    }
  });

  const datatableStateStorageKey = useMemo(
    () => `USER_${user.id}_WS_${account.id}_TIMESHEET_${getScopeName(scope)}_TABLE_STATE`,
    [scope, user.id, account.id]
  );
  const defaultDatatableState = useMemo(() => getDefaultTimesheetDatatableState(scope), [scope]);

  const {
    columnOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
    datatableSettings,
    setDatatableSetting,
    resetDatatableState,
  } = useDatatableState(defaultDatatableState, datatableStateStorageKey);

  const columns = useTimesheetColumnDefinitions(
    lastSelectedRowIndex,
    datatableSettings,
    datatableRows,
    !!account.features.tic_integrations
  );

  // Ensure parent linked work columns are in columnOrder and columnVisibility
  useEffect(() => {
    if (!account.features.tic_integrations) return;

    const parentColumns = [
      COLUMN_LINKED_WORK_ID,
      COLUMN_LINKED_WORK_TITLE,
      COLUMN_LINKED_WORK_STATUS,
      COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD,
    ];
    const missingFromOrder = parentColumns.filter(col => !columnOrder.includes(col));
    const missingFromVisibility = parentColumns.filter(col => !(col in columnVisibility));

    if (missingFromOrder.length > 0) {
      setColumnOrder(prev => [...prev, ...missingFromOrder]);
    }

    if (missingFromVisibility.length > 0) {
      setColumnVisibility(prev => {
        const newVisibility = { ...prev };
        missingFromVisibility.forEach(col => {
          newVisibility[col] = false; // default to hidden
        });
        return newVisibility;
      });
    }
  }, [
    account.features.tic_integrations,
    columnOrder,
    columnVisibility,
    setColumnOrder,
    setColumnVisibility,
  ]);

  const isSeparateTagStructure = datatableSettings.tagStyle?.tagStructure === 'separate';
  // The table's "Tags display" selection has to travel with the export, or the backend
  // falls back to the account default and always exports tag lists and tags (TT-5989).
  const exportTagsMode = datatableSettings.tagStyle
    ? tagDisplayToColumnMode[datatableSettings.tagStyle.tagDisplay]
    : undefined;

  useDeferredChangeEffect({
    comparisonValue: query,
    waitFor: hasInitialized,
    effect: () => {
      fetchTimesheetsForDatatable().then(fetchDayPropertiesForTimesheets);
      lastSelectedRowIndex.current = null;
    },
  });

  const weekStartsOn = useSelector(getStartOfWeek);
  const handleClearQuery = useCallback(
    () => setQuery(reportQueryFactory({}, { defaultValues: true, weekStartsOn })),
    [setQuery, weekStartsOn]
  );

  const handlePagination = useCallback(
    (page: number, limit: number) => {
      setQuery({ page, limit });
    },
    [setQuery]
  );

  const onDisabledFeatureClick = useCallback(
    () => showSubscriptionNoticeModal('featureBilledHoursRequiresUpgrade'),
    [showSubscriptionNoticeModal]
  );

  const selectedRowsBillable = useMemo(
    () => datatableRows.filter(row => selectionMap[row.id]).every(row => row.billable !== '-'),
    [selectionMap, datatableRows]
  );

  const handleClickTimeEntry = useCallback(
    (id: string) => {
      createHourDraftFromId(Number(id), {
        interaction: 'Click Time Entry',
      });
    },
    [createHourDraftFromId]
  );

  const handleSelectionChange: OnChangeFn<RowSelectionState> = useCallback(
    updaterOrValue => {
      const newRowSelection =
        updaterOrValue instanceof Function ? updaterOrValue(selectionMap) : updaterOrValue;

      const selectedIds: Array<number> = Object.keys(newRowSelection).map(Number);
      setSelection({ selectionKey: 'hourIds', value: selectedIds });
    },
    [selectionMap, setSelection]
  );

  const handleSelectAggregateFunction = useCallback(
    (columnId: string, functionType: AggregateFunctionName) => {
      setDatatableSetting('aggregateFunctions', {
        ...datatableSettings['aggregateFunctions'],
        [columnId]: functionType,
      });
    },
    [datatableSettings, setDatatableSetting]
  );

  const handleDatatableColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>(
    updaterOrValue => {
      const newVisibility =
        updaterOrValue instanceof Function ? updaterOrValue(columnVisibility) : updaterOrValue;
      setColumnVisibility(newVisibility);
    },
    [columnVisibility, setColumnVisibility]
  );

  const enableRowSelection = useCallback(
    (row: Row<TimesheetsDatatableRow>) => !!row.original.canSelect,
    []
  );

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const resetScrollPosition = React.useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, []);

  // Only reset scroll position when page or filters change
  useEffect(() => {
    resetScrollPosition();
  }, [query.page, query.filters, resetScrollPosition]);

  // Map parent column visibility to child columns
  const effectiveColumnVisibility = React.useMemo(() => {
    const visibility = { ...columnVisibility };
    columns.forEach(col => {
      const meta = col.meta as any;
      if (meta?.parentColumnId && typeof col.id === 'string') {
        const parentColumnId = meta.parentColumnId as string;
        if (parentColumnId in visibility) {
          visibility[col.id] = visibility[parentColumnId];
        }
      }
    });
    return visibility;
  }, [columnVisibility, columns]);

  // Expand parent columns into child columns for table and export
  const columnOrderIncludingTagLists = useMemo(() => {
    const newOrder = columnOrder.slice();

    // Expand tag columns
    if (isSeparateTagStructure) {
      const tagColumnIds = columns
        .filter(c => c.id?.startsWith('tag-'))
        .map(c => c.id)
        .filter(id => typeof id !== 'undefined');
      if (tagColumnIds.length > 0) {
        const tagsIndex = newOrder.indexOf('tags');
        if (tagsIndex !== -1) {
          newOrder.splice(tagsIndex, 1, ...tagColumnIds);
        }
      }
    }

    // Expand linked work columns
    const parentToChildMap: Record<string, string[]> = {
      linked_work_id: [],
      linked_work_title: [],
      linked_work_status: [],
      linked_work_jira_custom_field: [],
    };
    columns.forEach(col => {
      const meta = col.meta as any;
      if (meta?.parentColumnId && typeof col.id === 'string') {
        const parentId = meta.parentColumnId as string;
        if (parentId in parentToChildMap) {
          parentToChildMap[parentId].push(col.id);
        }
      }
    });
    // Replace parent columns with their children
    Object.entries(parentToChildMap).forEach(([parentId, childIds]) => {
      const parentIndex = newOrder.indexOf(parentId);
      if (parentIndex !== -1 && childIds.length > 0) {
        newOrder.splice(parentIndex, 1, ...childIds);
      }
    });

    return newOrder;
  }, [columnOrder, columns, isSeparateTagStructure]);

  const exportFilters = useMemo(() => {
    const visibleColumns = columnOrderIncludingTagLists.filter(
      c => effectiveColumnVisibility[c] === true && c !== 'selection'
    ) as Array<HourColumn>;

    const convertedColumns = convertTimesheetColumnIdToExportColumnId<HourColumn>(
      visibleColumns
    ).map(c =>
      isSeparateTagStructure && c === 'hour_labels' ? 'tag_headers' : c
    ) as Array<HourColumn>;

    // Replace indexed linked_work columns (linked_work_id_1, etc.) with parent columns (linked_work_id, etc.)
    // Backend expects parent columns in fields and handles expansion to indexed columns internally
    // We insert the parent column at the position of the first indexed column to preserve order
    const linkedWorkParentPositions = new Map<string, number>();
    const finalColumns: Array<HourColumn> = [];

    convertedColumns.forEach(col => {
      if (typeof col === 'string' && isLinkedWorkColumn(col)) {
        const parentCol = getLinkedWorkParentColumn(col);
        if (parentCol && !linkedWorkParentPositions.has(parentCol)) {
          // Track where we first see this parent column's children
          linkedWorkParentPositions.set(parentCol, finalColumns.length);
          finalColumns.push(parentCol as HourColumn);
        }
        // Skip indexed columns - parent will represent them
      } else {
        finalColumns.push(col);
      }
    });

    return {
      ...queryAsFilter,
      columns: finalColumns,
      taskColumns: convertTimesheetColumnIdToExportColumnId<TaskColumn>(
        queryAsFilter.taskColumns
      ) as Array<TaskColumn>,
    };
  }, [
    columnOrderIncludingTagLists,
    effectiveColumnVisibility,
    isSeparateTagStructure,
    queryAsFilter,
  ]);

  const isEmpty = !datatableRows.length;

  const { tableOptionsResetWarning, showOptionsResetWarning, closeOptionsResetWarning } =
    useTableOptionsResetWarning(user.id, defaultDatatableState);

  const columnOrderForOptions = useMemo(() => {
    // Filter out linked work columns if feature is not enabled
    if (!account.features.tic_integrations) {
      return columnOrder.filter(
        col =>
          col !== COLUMN_LINKED_WORK_ID &&
          col !== COLUMN_LINKED_WORK_TITLE &&
          col !== COLUMN_LINKED_WORK_STATUS &&
          col !== COLUMN_LINKED_WORK_JIRA_CUSTOM_FIELD
      );
    }
    return columnOrder;
  }, [columnOrder, account.features.tic_integrations]);

  return (
    <div className={classNames(styles.container, props.className)}>
      <div className={styles.filters}>
        {showOptionsResetWarning && (
          <div style={{ marginBottom: '16px' }}>
            <InlineMessage
              type="info"
              action={
                <div
                  onClick={closeOptionsResetWarning}
                  style={{ cursor: 'pointer', width: '24px' }}
                >
                  <Icon name="XClose" type="solid" />
                </div>
              }
            >
              <Emoji symbol="💡" label="" /> {tableOptionsResetWarning}
            </InlineMessage>
          </div>
        )}

        {!props.batchSelection.eventIds.length && (
          <ButtonRow>
            <ReportQueryDateOptions
              account={account}
              onSetQuery={updateFilters}
              query={query}
              size="small"
            />
            <ReportQueryFilters
              query={query}
              account={account}
              onClearQuery={handleClearQuery}
              onSetQuery={updateFilters}
              hasModifiedFilters={props.hasModifiedFilters}
              users={props.users}
              teams={props.teams}
              clients={props.clients}
              projects={props.projects}
              hourStates={props.hourStates}
              tags={props.tags}
              billingOptionsDropdown
              scope={scope}
              noClearButton={props.noClearButton}
              onDisabledFeatureClick={onDisabledFeatureClick}
            />
            <ButtonRow.Right>
              <QuickExportButton filters={exportFilters} tagsMode={exportTagsMode} />
              <DatatableOptionsDropdown
                columnOrder={columnOrderForOptions}
                columnVisibility={columnVisibility}
                settings={datatableSettings}
                onSettingChange={setDatatableSetting}
                onColumnOrderChange={setColumnOrder}
                onColumnVisibilityChange={setColumnVisibility}
                resetDatatableState={resetDatatableState}
              />
            </ButtonRow.Right>
          </ButtonRow>
        )}
        {!!props.batchSelection.eventIds.length && (
          <LoggedEntryBatchActions
            className={styles.batchActions}
            hasSelectedAllFiltered={datatableRows.length === selectionCount}
            selectedRowsBillable={selectedRowsBillable}
          />
        )}
      </div>
      <section
        ref={tableContainerRef}
        className={classNames(styles.timesheetTable, { [styles.empty]: isEmpty })}
      >
        {!isEmpty ? (
          <>
            <DataTable<TimesheetsDatatableRow>
              data={datatableRows}
              columns={columns}
              columnOrder={columnOrderIncludingTagLists}
              columnVisibility={effectiveColumnVisibility}
              onColumnVisibilityChange={handleDatatableColumnVisibilityChange}
              sorting={sorting}
              manualSorting
              onSortingChange={handleSortChange}
              getRowId={getRowId}
              enableRowSelection={enableRowSelection}
              onRowSelectionChange={handleSelectionChange}
              onRowClick={handleClickTimeEntry}
              rowSelection={selectionMap}
              zebraStripes={datatableSettings.zebraStripes}
              verticalBorders={datatableSettings.verticalBorders}
              stickyHeader
              fixedLayout
              hideFooter={true}
              stickyFooter={true}
              aggregateFooter={true}
              selectedAggregateFunctions={datatableSettings.aggregateFunctions}
              aggregateFunctions={aggregateFunctions}
              handleAggregateSelection={handleSelectAggregateFunction}
            />
            <TimesheetDatatableFooter
              totalCount={totalCount}
              pageSize={query.limit}
              currentPage={query.page}
              onPaginate={handlePagination}
            />
          </>
        ) : (
          <SplashMessage className={styles.empty}>
            <Clock />
            <h2 data-testid="message_container">
              {ui.isLoading || ui.isSaving ? 'Loading...' : 'No hours found'}
            </h2>
            {ui.isLoading || ui.isSaving ? null : (
              <p>Expand your search by adjusting the filters</p>
            )}
          </SplashMessage>
        )}
      </section>
    </div>
  );
};

export default connect(mapStateToProps, mapActionsToProps)(TimesheetPage);
